import { Core, Localization, Typography } from 'connex-cds';
import { useCallback, useEffect } from 'react';
import useThemeConfig from '../useThemeConfig';
import { Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { tickets } from '../../../../../api';
import {
  DRIVER_TICKETS_TODAY_QUERY,
  useListDriverTickets,
  isPendingTicket,
  findNextPendingDriverTicket,
} from '../../../../../query-hooks/tickets';
import { useFeatureFlags } from '../../../../../providers/feature-flags-provider';
import { FLAG_LOAD_ASSIGNMENTS } from '../../../../../providers/feature-flags-provider/constants';
import * as S from './styles';

const sendMessage = message => {
  window.postMessage(message, '*');
};

const ActiveTicket = () => {
  const { entityRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const excludeCrn = location?.state?.excludeCrn;
  const { getFlagValue } = useFeatureFlags() ?? {};
  // Picking "View Active Ticket" from the menu must keep the waiting screen; only a hand-off
  // from landing or from a finished/voided ticket may fall through to the schedule.
  const autoRouted = Boolean(location?.state?.autoRouted || excludeCrn);

  const driverTickets = useListDriverTickets(DRIVER_TICKETS_TODAY_QUERY);

  const loadTicket = ticket => {
    if (ticket) {
      const message = { type: 'ticket', active: true, crn: ticket?.crn };
      navigate(`${ticket?.vehicleId}`, {
        state: {
          type: 'ticket',
          active: true,
          crn: ticket?.crn,
        },
      });
      sendMessage(message);
    }
  };

  const findPendingTicket = useCallback(
    async () => findNextPendingDriverTicket(entityRef, excludeCrn),
    [entityRef, excludeCrn]
  );

  const pendingTicket = driverTickets?.isFetched
    ? driverTickets?.data?.findLast?.(item => isPendingTicket(item, excludeCrn))
    : undefined;

  useEffect(() => {
    loadTicket(pendingTicket);
  }, [pendingTicket]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const ticket = await findPendingTicket();
      loadTicket(ticket);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [findPendingTicket]);

  const handOffToLoadAssignments =
    autoRouted &&
    Boolean(getFlagValue?.(FLAG_LOAD_ASSIGNMENTS)) &&
    driverTickets?.isSuccess &&
    !pendingTicket;

  if (handOffToLoadAssignments) {
    return <Navigate to={`/${entityRef}/load-assignments`} replace />;
  }

  return (
    <Core.Spinner spin={tickets?.isLoading || (autoRouted && !driverTickets?.isFetched)}>
      <S.FormStyle>
        <S.LabelStyle>
          <Typography.H1>
            <Localization.Translate stringId="waitingForTicket" data-testid="waitingForTicket" />
          </Typography.H1>
        </S.LabelStyle>
      </S.FormStyle>
    </Core.Spinner>
  );
};

export default useThemeConfig(ActiveTicket);

